import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { ConditionalObject } from 'common/types/utilityTypes'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { Repeat04Icon } from 'src/editor/shared/icons/repeat-04-icon'
import {
  ButtonSizeEnum,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { twMerge } from 'tailwind-merge'
import {
  FontSizeControl,
  FontSizeControlProps,
} from '../../controls/font-size-control'
import {
  LetterSpacingControl,
  LetterSpacingControlProps,
} from '../../controls/letter-spacing-control'
import {
  LineHeightControl,
  LineHeightControlProps,
} from '../../controls/line-height-control'
import {
  TextAlignControl,
  TextAlignControlProps,
} from '../../controls/text-align-control'
import {
  EntityFontSelectorControl,
  EntityFontSelectorControlProps,
} from './entity-font-selector-control'

type TypographyGroupProps<
  Entity extends Record<string, any>,
  FontFamilyKey extends keyof Entity,
  FontWeightKey extends keyof Entity,
  FontStyleKey extends keyof Entity,
  FontFileIdKey extends keyof Entity,
  MobileFontFamilyKey extends keyof Entity,
  MobileFontWeightKey extends keyof Entity,
  MobileFontStyleKey extends keyof Entity,
  MobileFontFileIdKey extends keyof Entity,
  FontSizeKey extends keyof Entity,
  MobileFontSizeKey extends keyof Entity,
  LineHeightKey extends keyof Entity,
  MobileLineHeightKey extends keyof Entity,
  TextAlignKey extends keyof Entity,
  MobileTextAlignKey extends keyof Entity,
  LetterSpacingKey extends keyof Entity,
  MobileLetterSpacingKey extends keyof Entity,
> = ConditionalObject<
  Omit<
    EntityFontSelectorControlProps<
      Entity,
      FontFamilyKey,
      FontWeightKey,
      FontStyleKey,
      FontFileIdKey,
      MobileFontFamilyKey,
      MobileFontWeightKey,
      MobileFontStyleKey,
      MobileFontFileIdKey
    >,
    'object' | 'updateObject' | 'hideLabel' | 'labelText' | 'isRootSetting'
  >,
  'hideFontSelector'
> &
  ConditionalObject<
    Pick<FontSizeControlProps, 'fontSize' | 'mobileFontSize'> & {
      fontSizeKey: FontSizeKey
      mobileFontSizeKey?: MobileFontSizeKey
      updateFontSize: FontSizeControlProps['update']
      updateMobileFontSize?: FontSizeControlProps['mobileUpdate']
    },
    'hideFontSize'
  > &
  ConditionalObject<
    Pick<LineHeightControlProps, 'lineHeight' | 'mobileLineHeight'> & {
      lineHeightKey: LineHeightKey
      mobileLineHeightKey?: MobileLineHeightKey
      updateLineHeight: LineHeightControlProps['update']
      updateMobileLineHeight?: LineHeightControlProps['mobileUpdate']
    },
    'hideLineHeight'
  > &
  ConditionalObject<
    Pick<TextAlignControlProps, 'textAlign' | 'mobileTextAlign'> & {
      textAlignKey: TextAlignKey
      mobileTextAlignKey?: MobileTextAlignKey
      updateTextAlign: TextAlignControlProps['update']
      updateMobileTextAlign?: TextAlignControlProps['mobileUpdate']
    },
    'hideTextAlign'
  > &
  ConditionalObject<
    Pick<LetterSpacingControlProps, 'letterSpacing' | 'mobileLetterSpacing'> & {
      letterSpacingKey: LetterSpacingKey
      mobileLetterSpacingKey?: MobileLetterSpacingKey
      updateLetterSpacing: LetterSpacingControlProps['update']
      updateMobileLetterSpacing?: LetterSpacingControlProps['mobileUpdate']
    },
    'hideLetterSpacing'
  > & {
    entity: Entity
    updateEntity: (value: Entity) => void
    hideTitle?: boolean
    title?: string
    withoutHorizontalLine?: boolean
  }

export const TypographyGroup = <
  Entity extends Record<string, any>,
  FontFamilyKey extends keyof Entity,
  FontWeightKey extends keyof Entity,
  FontStyleKey extends keyof Entity,
  FontFileIdKey extends keyof Entity,
  MobileFontFamilyKey extends keyof Entity,
  MobileFontWeightKey extends keyof Entity,
  MobileFontStyleKey extends keyof Entity,
  MobileFontFileIdKey extends keyof Entity,
  FontSizeKey extends keyof Entity,
  MobileFontSizeKey extends keyof Entity,
  LineHeightKey extends keyof Entity,
  MobileLineHeightKey extends keyof Entity,
  TextAlignKey extends keyof Entity,
  MobileTextAlignKey extends keyof Entity,
  LetterSpacingKey extends keyof Entity,
  MobileLetterSpacingKey extends keyof Entity,
>({
  entity,
  fontFamilyKey,
  fontWeightKey,
  fontStyleKey,
  fontFileIdKey,
  mobileFontFamilyKey,
  mobileFontWeightKey,
  mobileFontStyleKey,
  mobileFontFileIdKey,
  fontFamily,
  mobileFontFamily,
  fontWeight,
  mobileFontWeight,
  fontStyle,
  mobileFontStyle,
  globalFontFamily,
  mobileGlobalFontFamily,
  hideFontSelector,
  fontSizeKey,
  lineHeightKey,
  textAlignKey,
  letterSpacingKey,
  mobileFontSizeKey,
  mobileLineHeightKey,
  mobileTextAlignKey,
  mobileLetterSpacingKey,
  hideFontSize,
  fontSize,
  mobileFontSize,
  updateFontSize,
  updateMobileFontSize,
  hideLineHeight,
  lineHeight,
  mobileLineHeight,
  updateLineHeight,
  updateMobileLineHeight,
  hideTextAlign,
  textAlign,
  mobileTextAlign,
  updateTextAlign,
  updateMobileTextAlign,
  hideLetterSpacing,
  letterSpacing,
  mobileLetterSpacing,
  updateLetterSpacing,
  updateMobileLetterSpacing,
  updateEntity,
  hideTitle,
  title,
  withoutHorizontalLine,
}: TypographyGroupProps<
  Entity,
  FontFamilyKey,
  FontWeightKey,
  FontStyleKey,
  FontFileIdKey,
  MobileFontFamilyKey,
  MobileFontWeightKey,
  MobileFontStyleKey,
  MobileFontFileIdKey,
  FontSizeKey,
  MobileFontSizeKey,
  LineHeightKey,
  MobileLineHeightKey,
  TextAlignKey,
  MobileTextAlignKey,
  LetterSpacingKey,
  MobileLetterSpacingKey
>) => {
  const { isMobile } = useDeviceModeContext()

  const showFontSelector = !hideFontSelector
  const showFontSize = !hideFontSize
  const showLineHeight = !hideLineHeight
  const showTextAlign = !hideTextAlign
  const showLetterSpacing = !hideLetterSpacing

  // TODO: how to check if the entity overwrites the global settings?
  const showResetTypographyToGlobalSettings = true

  const resetProperty = (key: keyof Entity | undefined) => {
    return key && entity[key] ? { [key]: undefined } : {}
  }

  const onResetTypography = () => {
    if (isMobile) {
      updateEntity({
        ...entity,
        ...resetProperty(mobileFontFamilyKey),
        ...resetProperty(mobileFontWeightKey),
        ...resetProperty(mobileFontStyleKey),
        ...resetProperty(mobileFontFileIdKey),
        ...resetProperty(mobileFontSizeKey),
        ...resetProperty(mobileLineHeightKey),
        ...resetProperty(mobileTextAlignKey),
        ...resetProperty(mobileLetterSpacingKey),
      })
    } else {
      updateEntity({
        ...entity,
        ...resetProperty(fontFamilyKey),
        ...resetProperty(fontWeightKey),
        ...resetProperty(fontStyleKey),
        ...resetProperty(fontFileIdKey),
        ...resetProperty(fontSizeKey),
        ...resetProperty(lineHeightKey),
        ...resetProperty(textAlignKey),
        ...resetProperty(letterSpacingKey),
      })
    }
  }

  return (
    <>
      {!hideTitle && (
        <GroupTitle
          withoutHorizontalLine={withoutHorizontalLine}
          afterChildren={
            showResetTypographyToGlobalSettings && (
              <GhostIconButton
                className="!my-[-4px]"
                size={ButtonSizeEnum.Small}
                onClick={onResetTypography}
                icon={<Repeat04Icon />}
              />
            )
          }
        >
          {title || 'typography'}
        </GroupTitle>
      )}
      <div
        className={twMerge(
          '[&>*]:!m-0 mt-4 grid grid-cols-2 gap-3',
          '[&>*:nth-child(1)]:col-span-2 [&>*:nth-child(2)]:col-span-2',
        )}
      >
        {showFontSelector && (
          <EntityFontSelectorControl
            object={entity}
            fontFamilyKey={fontFamilyKey}
            fontWeightKey={fontWeightKey}
            fontStyleKey={fontStyleKey}
            fontFileIdKey={fontFileIdKey}
            mobileFontFamilyKey={mobileFontFamilyKey}
            mobileFontWeightKey={mobileFontWeightKey}
            mobileFontStyleKey={mobileFontStyleKey}
            mobileFontFileIdKey={mobileFontFileIdKey}
            updateObject={updateEntity}
            hideLabel
            fontFamily={fontFamily}
            mobileFontFamily={mobileFontFamily}
            fontWeight={fontWeight}
            mobileFontWeight={mobileFontWeight}
            fontStyle={fontStyle}
            mobileFontStyle={mobileFontStyle}
            globalFontFamily={globalFontFamily}
            mobileGlobalFontFamily={mobileGlobalFontFamily}
          />
        )}
        {showFontSize && (
          <FontSizeControl
            fontSize={fontSize}
            mobileFontSize={mobileFontSize}
            update={updateFontSize}
            mobileUpdate={updateMobileFontSize}
            className="!col-span-1"
          />
        )}
        {showLineHeight && (
          <LineHeightControl
            lineHeight={lineHeight}
            mobileLineHeight={mobileLineHeight}
            update={updateLineHeight}
            mobileUpdate={updateMobileLineHeight}
            className="!col-span-1"
          />
        )}
        {showTextAlign && (
          <TextAlignControl
            textAlign={textAlign}
            mobileTextAlign={mobileTextAlign}
            update={updateTextAlign}
            mobileUpdate={updateMobileTextAlign}
            className="!col-span-1"
          />
        )}
        {showLetterSpacing && (
          <LetterSpacingControl
            letterSpacing={letterSpacing}
            mobileLetterSpacing={mobileLetterSpacing}
            update={updateLetterSpacing}
            mobileUpdate={updateMobileLetterSpacing}
            wrapperClassName="!col-span-2"
          />
        )}
      </div>
    </>
  )
}
